import type { Role } from '@wrapforce/shared/constants';
import type { TourDefinition } from './types';

// VITE_ASSISTANT_ENABLED is a build-time constant, not a runtime value, so we gate
// the assistant's tour content at module scope: when it is off, the assistant step
// and tour are omitted entirely rather than pointing at a button that never renders.
const ASSISTANT_ENABLED = import.meta.env.VITE_ASSISTANT_ENABLED === 'true';

// Integrations, and the quote widget inside it, are owner-only in the sidebar
// (nav-items.ts), so the steps that point at them are dropped for everyone else.
const OWNER_ONLY: Role[] = ['manager', 'front_desk', 'installer'];

export const TOURS: TourDefinition[] = [
  {
    id: 'getting-started',
    nameKey: 'tours.getting-started.name',
    steps: [
      { anchor: null, route: '/', titleKey: 'tours.getting-started.steps.welcome.title', bodyKey: 'tours.getting-started.steps.welcome.body' },
      { anchor: 'nav:/leads', titleKey: 'tours.getting-started.steps.leads.title', bodyKey: 'tours.getting-started.steps.leads.body' },
      { anchor: 'nav:/quotes', titleKey: 'tours.getting-started.steps.quotes.title', bodyKey: 'tours.getting-started.steps.quotes.body' },
      { anchor: 'nav:/jobs', titleKey: 'tours.getting-started.steps.jobs.title', bodyKey: 'tours.getting-started.steps.jobs.body' },
      { anchor: 'nav:/customers', titleKey: 'tours.getting-started.steps.customers.title', bodyKey: 'tours.getting-started.steps.customers.body' },
      { anchor: 'nav:/calendar', titleKey: 'tours.getting-started.steps.calendar.title', bodyKey: 'tours.getting-started.steps.calendar.body' },
      { anchor: 'topbar-quick-add', titleKey: 'tours.getting-started.steps.quickAdd.title', bodyKey: 'tours.getting-started.steps.quickAdd.body' },
      ...(ASSISTANT_ENABLED
        ? [{ anchor: 'assistant-launcher', titleKey: 'tours.getting-started.steps.assistant.title', bodyKey: 'tours.getting-started.steps.assistant.body' }]
        : []),
      { anchor: 'nav:/integrations', hiddenFor: OWNER_ONLY, titleKey: 'tours.getting-started.steps.quoteWidget.title', bodyKey: 'tours.getting-started.steps.quoteWidget.body' },
      { anchor: 'topbar-help', titleKey: 'tours.getting-started.steps.help.title', bodyKey: 'tours.getting-started.steps.help.body' },
    ],
  },
  {
    id: 'leads',
    nameKey: 'tours.leads.name',
    autoRoute: '/leads',
    steps: [
      { anchor: 'nav:/leads', route: '/leads', titleKey: 'tours.leads.steps.nav.title', bodyKey: 'tours.leads.steps.nav.body' },
      { anchor: 'page-leads-board', titleKey: 'tours.leads.steps.board.title', bodyKey: 'tours.leads.steps.board.body' },
      { anchor: 'topbar-quick-add', titleKey: 'tours.leads.steps.quickAdd.title', bodyKey: 'tours.leads.steps.quickAdd.body' },
      { anchor: null, titleKey: 'tours.leads.steps.done.title', bodyKey: 'tours.leads.steps.done.body' },
    ],
  },
  {
    id: 'quotes',
    nameKey: 'tours.quotes.name',
    autoRoute: '/quotes',
    steps: [
      { anchor: 'nav:/quotes', route: '/quotes', titleKey: 'tours.quotes.steps.nav.title', bodyKey: 'tours.quotes.steps.nav.body' },
      { anchor: 'page-quotes-list', titleKey: 'tours.quotes.steps.list.title', bodyKey: 'tours.quotes.steps.list.body' },
      { anchor: 'topbar-quick-add', titleKey: 'tours.quotes.steps.quickAdd.title', bodyKey: 'tours.quotes.steps.quickAdd.body' },
      { anchor: null, titleKey: 'tours.quotes.steps.done.title', bodyKey: 'tours.quotes.steps.done.body' },
    ],
  },
  {
    id: 'pricing',
    nameKey: 'tours.pricing.name',
    autoRoute: '/pricing',
    steps: [
      { anchor: 'nav:/pricing', route: '/pricing', titleKey: 'tours.pricing.steps.nav.title', bodyKey: 'tours.pricing.steps.nav.body' },
      { anchor: 'page-pricing-tabs', titleKey: 'tours.pricing.steps.tabs.title', bodyKey: 'tours.pricing.steps.tabs.body' },
      { anchor: null, titleKey: 'tours.pricing.steps.done.title', bodyKey: 'tours.pricing.steps.done.body' },
    ],
  },
  {
    id: 'inventory',
    nameKey: 'tours.inventory.name',
    autoRoute: '/inventory',
    steps: [
      { anchor: 'nav:/inventory', route: '/inventory', titleKey: 'tours.inventory.steps.nav.title', bodyKey: 'tours.inventory.steps.nav.body' },
      { anchor: 'page-inventory-add', titleKey: 'tours.inventory.steps.add.title', bodyKey: 'tours.inventory.steps.add.body' },
      { anchor: null, titleKey: 'tours.inventory.steps.done.title', bodyKey: 'tours.inventory.steps.done.body' },
    ],
  },
  ...(ASSISTANT_ENABLED
    ? [
        {
          id: 'assistant',
          nameKey: 'tours.assistant.name',
          steps: [
            { anchor: 'assistant-launcher', route: '/', titleKey: 'tours.assistant.steps.open.title', bodyKey: 'tours.assistant.steps.open.body' },
            { anchor: 'assistant-launcher', titleKey: 'tours.assistant.steps.ask.title', bodyKey: 'tours.assistant.steps.ask.body' },
            { anchor: 'assistant-launcher', titleKey: 'tours.assistant.steps.confirm.title', bodyKey: 'tours.assistant.steps.confirm.body' },
            { anchor: null, titleKey: 'tours.assistant.steps.done.title', bodyKey: 'tours.assistant.steps.done.body' },
          ],
        },
      ]
    : []),
  {
    id: 'integrations',
    nameKey: 'tours.integrations.name',
    autoRoute: '/integrations',
    hiddenFor: OWNER_ONLY,
    steps: [
      { anchor: 'nav:/integrations', route: '/integrations', titleKey: 'tours.integrations.steps.nav.title', bodyKey: 'tours.integrations.steps.nav.body' },
      { anchor: 'page-integrations-grid', titleKey: 'tours.integrations.steps.grid.title', bodyKey: 'tours.integrations.steps.grid.body' },
      { anchor: 'integration-quote-widget', titleKey: 'tours.integrations.steps.quoteWidget.title', bodyKey: 'tours.integrations.steps.quoteWidget.body' },
      { anchor: null, titleKey: 'tours.integrations.steps.done.title', bodyKey: 'tours.integrations.steps.done.body' },
    ],
  },
  {
    id: 'settings',
    nameKey: 'tours.settings.name',
    autoRoute: '/settings',
    steps: [
      { anchor: 'nav:/settings', route: '/settings', titleKey: 'tours.settings.steps.nav.title', bodyKey: 'tours.settings.steps.nav.body' },
      { anchor: 'page-settings-nav', titleKey: 'tours.settings.steps.nav2.title', bodyKey: 'tours.settings.steps.nav2.body' },
      { anchor: null, titleKey: 'tours.settings.steps.done.title', bodyKey: 'tours.settings.steps.done.body' },
    ],
  },
];
